// oxlint-disable typescript/no-unsafe-type-assertion

import { setup } from 'xstate';

import { type Context, type Event, type Input } from './scrollspy-virtualized.types';
import { Actions, createInitialContext, Guards } from './setup';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves the active scrollspy target for a virtualized list.
 *
 * A jump records a pending target, and viewability is ignored until the list
 * reports it. Virtualized lists report the rows they pass on the way, including
 * the ones rendered before an initial scroll lands, so an ungated report would
 * activate whatever the list happened to be showing.
 */
export const scrollspyVirtualizedMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Event,
    input: {} as Input,
  },
  actions: {
    activateRequestedTarget: Actions.activateRequestedTarget,
    activateVisibleTarget: Actions.activateVisibleTarget,
    clearPendingTarget: Actions.clearPendingTarget,
    scrollToRequestedTarget: Actions.scrollToRequestedTarget,
    onActiveTargetChange: Actions.onActiveTargetChange,
  },
  guards: {
    checkIfNoPendingTarget: Guards.checkIfNoPendingTarget,
    checkIfPendingTargetReached: Guards.checkIfPendingTargetReached,
    checkIfVisibleTargetChanged: Guards.checkIfVisibleTargetChanged,
  },
  delays: {
    PENDING_TARGET_TIMEOUT: 500,
  },
}).createMachine({
  id: 'scrollspy-virtualized',
  context: createInitialContext,
  initial: 'awaiting-target',
  states: {
    'awaiting-target': {
      description: 'Holding the requested target until the list reports it as visible.',
      always: { guard: 'checkIfNoPendingTarget', target: 'tracking' },
      on: {
        SET_VISIBLE_TARGETS: {
          guard: 'checkIfPendingTargetReached',
          target: 'tracking',
          actions: ['clearPendingTarget'],
        },
      },
      after: {
        PENDING_TARGET_TIMEOUT: { target: 'tracking', actions: ['clearPendingTarget'] },
      },
    },
    tracking: {
      description: 'The first visible target drives the active one.',
      on: {
        SET_VISIBLE_TARGETS: {
          guard: 'checkIfVisibleTargetChanged',
          actions: ['activateVisibleTarget', 'onActiveTargetChange'],
        },
      },
    },
  },
  on: {
    SCROLL_TO_TARGET: {
      target: '.awaiting-target',
      actions: ['activateRequestedTarget', 'scrollToRequestedTarget', 'onActiveTargetChange'],
    },
  },
});
